/**
 * useScrape Hook
 * 
 * React hook for scrape operations using ScrapeRepository.
 * Provides loading states, error handling, and real-time updates.
 * 
 * Usage:
 * - Single scrape: useScrape(scrapeId)
 * - Operations only: useScrape()
 */

import { useState, useEffect, useCallback } from 'react';
import { ScrapeRepository } from '../models/ScrapeRepository';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../firebase';

const repo = new ScrapeRepository(db, functions);

export const useScrape = (scrapeId = null) => {
  const [scrape, setScrape] = useState(null);
  const [loading, setLoading] = useState(!!scrapeId);
  const [error, setError] = useState(null);

  // Fetch single scrape with real-time updates
  useEffect(() => {
    if (!scrapeId) return;

    setLoading(true);
    setError(null);

    // Timeout guard: if onSnapshot never fires (stale Firestore channel), stop spinning after 15s
    const timeoutId = setTimeout(() => {
      setLoading((current) => {
        if (current) {
          console.warn('🔧 [NEW ARCH] useScrape: onSnapshot timed out for', scrapeId);
          setError(new Error('Firestore connection timed out. Hard-refresh the page (Cmd+Shift+R) to fix.'));
          return false;
        }
        return current;
      });
    }, 15000);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'discovery_scrapes', scrapeId),
      (doc) => {
        clearTimeout(timeoutId);
        if (doc.exists()) {
          setScrape({ id: doc.id, ...doc.data() });
          setError(null);
        } else {
          setScrape(null);
          setError(new Error('Scrape not found'));
        }
        setLoading(false);
      },
      (err) => {
        clearTimeout(timeoutId);
        console.error('Error fetching scrape:', err);
        setError(err);
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [scrapeId]);

  // Delete scrape — via the admin-only deleteScrape function, which clears the scrape
  // from its properties and mailshots (the properties themselves are kept)
  const deleteScrape = useCallback(async () => {
    if (!scrapeId) throw new Error('No scrape ID provided');
    
    try {
      await repo.delete(scrapeId);
    } catch (err) {
      console.error('Error deleting scrape:', err);
      throw err;
    }
  }, [scrapeId]);

  // Merge scrapes (combine multiple scrapes into target via Cloud Function)
  const mergeScrapes = useCallback(async (sourceIds, targetId, options = {}) => {
    try {
      await repo.merge(sourceIds, targetId, options);
    } catch (err) {
      console.error('Error merging scrapes:', err);
      throw err;
    }
  }, []);

  // Split scrape (move properties to new scrape via Cloud Function)
  const splitScrape = useCallback(async (propertyIds, newScrapeName) => {
    if (!scrapeId) throw new Error('No scrape ID provided');
    
    try {
      const newScrapeId = await repo.split(scrapeId, propertyIds, newScrapeName);
      return newScrapeId;
    } catch (err) {
      console.error('Error splitting scrape:', err);
      throw err;
    }
  }, [scrapeId]);

  // Recalculate scrape statistics (rebuild from actual property data via Cloud Function)
  const recalculateStats = useCallback(async () => {
    if (!scrapeId) throw new Error('No scrape ID provided');
    
    try {
      await repo.recalculateStats(scrapeId);
    } catch (err) {
      console.error('Error recalculating stats:', err);
      throw err;
    }
  }, [scrapeId]);

  // Archive scrape
  const archive = useCallback(async () => {
    if (!scrapeId) throw new Error('No scrape ID provided');
    
    try {
      await repo.archive(scrapeId);
    } catch (err) {
      console.error('Error archiving scrape:', err);
      throw err;
    }
  }, [scrapeId]);

  // Unarchive scrape
  const unarchive = useCallback(async () => {
    if (!scrapeId) throw new Error('No scrape ID provided');
    
    try {
      await repo.unarchive(scrapeId);
    } catch (err) {
      console.error('Error unarchiving scrape:', err);
      throw err;
    }
  }, [scrapeId]);

  // Cancel running scrape
  const cancel = useCallback(async () => {
    if (!scrapeId) throw new Error('No scrape ID provided');
    
    try {
      return await repo.cancel(scrapeId);
    } catch (err) {
      console.error('Error cancelling scrape:', err);
      throw err;
    }
  }, [scrapeId]);

  // Update scrape fields
  const update = useCallback(async (updates) => {
    if (!scrapeId) throw new Error('No scrape ID provided');
    
    try {
      await repo.update(scrapeId, updates);
    } catch (err) {
      console.error('Error updating scrape:', err);
      throw err;
    }
  }, [scrapeId]);

  // Force refresh (mainly for testing)
  const refresh = useCallback(() => {
    if (!scrapeId) return;
    setLoading(true);
    // onSnapshot will automatically trigger with latest data
  }, [scrapeId]);

  return {
    scrape,
    loading,
    error,
    deleteScrape,
    mergeScrapes,
    splitScrape,
    recalculateStats,
    archive,
    unarchive,
    cancel,
    update,
    refresh
  };
};
